import { useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import {
  CenteredBox,
  DefaultButton,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { DangerButton } from "@components/buttons/DangerButton";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { Item } from "@components/SelectionList";
import { withPermissions } from "@components/withPermissions";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  useClearDeploymentRingConfigurationCache,
  useDeploymentRings
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { RingList } from "./RingList";
import { DevicesAssigned } from "./rollout/devices-assigned/DevicesAssigned";
import { RolloutList } from "./rollout/RolloutList";
import { DeploymentRingSelectorHost } from "./selector";
import { ViewConfigurationModal } from "./ViewConfigurationModal";

const CLEAR_DEPLOYMENT_CACHE_TOOLTIP =
  "Clears the deployment ring block distributed cache for this deployment ring.";

const VIEW_DEPLOYMENT_CACHE_TOOLTIP =
  "View the deployment ring block distributed cache for this deployment ring.";

const DeployRingsBase: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { ringId } = useParams<{ ringId: string }>();

  const selectRing = (item: Item) => {
    const to = generatePath("/system/plt/deployment-rings/:ringId", {
      ringId: item.id
    });
    navigate(to);
  };

  const [showViewConfigBlock, setShowViewConfigBlock] = useState<boolean>(
    false
  );

  const clearDeviceFieldConfigurationCache = useClearDeploymentRingConfigurationCache(
    ringId!
  );

  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const deploymentRingsQuery = useDeploymentRings();
  return (
    <>
      <SectionTitle>Deployment Rings</SectionTitle>
      <QueryStateIndicator {...deploymentRingsQuery}>
        {rings => {
          const ring = rings.find(x => x.id === ringId);

          return (
            <Stack
              horizontal
              verticalFill
              tokens={{ childrenGap: theme.spacing.m }}
            >
              <Stack styles={{ root: { width: "300px" } }}>
                <RingList
                  deploymentRings={rings}
                  deploymentRing={ring}
                  onSelectedItemChange={selectRing}
                />
              </Stack>
              <Stack verticalFill styles={{ root: { width: "100%" } }}>
                {ring ? (
                  <Stack
                    verticalFill
                    tokens={{ childrenGap: theme.spacing.s1 }}
                  >
                    <Stack
                      horizontal
                      styles={{ root: { justifyContent: "space-between" } }}
                    >
                      <Stack>
                        <Text variant="xLarge">
                          {ring.displayName} - {ring.code}
                        </Text>
                        <Text>{ring.description}</Text>
                      </Stack>

                      <Stack.Item align="end">
                        <Stack
                          horizontal
                          tokens={{ childrenGap: theme.spacing.s1 }}
                        >
                          <TooltipHost content={VIEW_DEPLOYMENT_CACHE_TOOLTIP}>
                            <DefaultButton
                              onClick={() => setShowViewConfigBlock(true)}
                            >
                              View Cached Deployment Block
                            </DefaultButton>
                          </TooltipHost>

                          <TooltipHost content={CLEAR_DEPLOYMENT_CACHE_TOOLTIP}>
                            <DangerButton
                              disabled={!hasDeployPermission}
                              title={
                                !hasDeployPermission
                                  ? NO_ACTION_PERMISSION
                                  : undefined
                              }
                              onClick={clearDeviceFieldConfigurationCache}
                            >
                              Clear Cached Deployment Block
                            </DangerButton>
                          </TooltipHost>
                        </Stack>
                      </Stack.Item>
                    </Stack>

                    <Stack
                      tokens={{ childrenGap: theme.spacing.m }}
                      styles={{
                        root: { overflow: "auto", height: "100%" }
                      }}
                    >
                      <DeploymentRingSelectorHost deploymentRing={ring} />

                      <Stack
                        horizontal
                        tokens={{ childrenGap: theme.spacing.m }}
                      >
                        <RolloutList deploymentRingId={ring.id} />
                        <DevicesAssigned deploymentRingId={ring.id} />
                      </Stack>
                    </Stack>
                  </Stack>
                ) : (
                  <CenteredBox>
                    <Text>Please select a deployment ring</Text>
                  </CenteredBox>
                )}
              </Stack>
            </Stack>
          );
        }}
      </QueryStateIndicator>

      <ViewConfigurationModal
        show={showViewConfigBlock}
        ringId={ringId!}
        onDismiss={() => setShowViewConfigBlock(false)}
      />
    </>
  );
};

export const DeployRings = withPermissions(
  DeployRingsBase,
  [
    Permissions.PltFieldManagementRead,
    Permissions.PltFieldManagementWrite,
    Permissions.PltFieldManagementDeploy
  ],
  "or"
);
